import React, { useState, useEffect, useMemo, useCallback, useContext, useRef } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearchLocation } from '@fortawesome/free-solid-svg-icons';
import LoaderComponent from '../../Shared/Loader';
import { AuthContext } from '../../Providers/UserProvider';
import { rejectionMessage } from '../../utils/rejectionMessage';
import { MISSING_COMPANY_CLAIM_ERROR, companyIdFromClaims } from '../../utils/companyClaim';
import StatusEmptyCard from './StatusEmptyCard';
import {
    STATUS_EMPTY_JOBS,
    STATUS_ERROR_JOBS,
    STATUS_OFFLINE,
    statusAvailabilityHeadline,
    statusFilterEmptyCard,
    statusListCopy,
    statusSurfaceState,
} from './statusSurface';

/** Missing `name` matches as ''. `null` still stringifies to "null". A blank trimmed term matches every shop. The term is matched case-insensitively. */
export function statusShopMatchesSearch(name: unknown, filterTerm?: string): boolean {
    const term = (filterTerm || '').trim().toLowerCase();
    if (!term) return true;
    const searchable = name === undefined ? '' : JSON.stringify(name).toLowerCase();
    return searchable.includes(term);
}

/**
 * Leaflet already coerces numeric strings (`isNaN` + unary plus). Missing, null,
 * and non-numeric values throw while `MapContainer` creates the map, so those shops
 * keep their card and skip the map. Parsed 0,0 is a bad address and skips the map
 * the same way. A single zero is a real coordinate.
 */
export function statusShopMapCenter(lat: unknown, lng: unknown): [number, number] | null {
    const latitude = finiteShopCoordinate(lat);
    const longitude = finiteShopCoordinate(lng);
    if (latitude === null || longitude === null) return null;
    // A failed geocode is usually stored as the 0,0 default. One zero is a real coordinate.
    if (latitude === 0 && longitude === 0) return null;
    return [latitude, longitude];
}

function finiteShopCoordinate(value: unknown): number | null {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
        return Number(value);
    }
    return null;
}

/**
 * A GPS coordinate is missing only when `Number.isFinite` is false (including
 * non-numbers). Both finite values are written, and a single 0 is a real fix.
 * Exactly 0,0 is the same bad default `statusShopMapCenter` skips.
 */
export function statusGpsWrite(lat: unknown, lng: unknown): { lat: number; lng: number } | null {
    if (typeof lat !== 'number' || typeof lng !== 'number') return null;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat === 0 && lng === 0) return null;
    return { lat, lng };
}

function statusWriteMessage(error: unknown, fallback: string): string {
    const detail = rejectionMessage(error, '');
    return detail ? `${fallback} ${detail}` : fallback;
}

const StatusPage = () => {
    const { techId, tech, shopsRef, jobsRef, techsRef, user } = useContext(AuthContext);
    const companyId = companyIdFromClaims((user as { claims?: { companyId?: unknown } } | null)?.claims);

    const [selectedLocation, setSelectedLocation] = useState<any>({});

    const [locations, setlocations] = useState<any>([]);

    const [userLat, setUserLat] = useState<number>();

    const [userLng, setUserLng] = useState<number>();

    const [jobs, setJobs] = useState<any>([]);

    const [filterTerm, setFilterTerm] = useState<string>();

    const shopsRefForSearch = useRef(shopsRef);

    const [reloadToken, setReloadToken] = useState(0);

    const [shopsLoading, setShopsLoading] = useState(true);

    const [jobsLoading, setJobsLoading] = useState(true);

    const [shopsError, setShopsError] = useState('');

    const [selectedShopError, setSelectedShopError] = useState('');

    const [jobsError, setJobsError] = useState('');

    const [writeError, setWriteError] = useState('');

    const writeErrorSourceRef = useRef('');

    /** Synchronous lock for Available, Unavailable, and Select Location. GPS writes stay outside this lock. */
    const statusControlLock = useRef(false);
    const [statusControlBusy, setStatusControlBusy] = useState(false);

    const gpsWriteGenerationRef = useRef(0);

    const jobsReadyRef = useRef(false);

    const [offline, setOffline] = useState(typeof navigator !== 'undefined' ? navigator.onLine === false : false);

    const isAvailable = useMemo(() => tech.status, [tech]);

    const selectedLocationId = useMemo(() => tech.location, [tech]);

    const retry = useCallback(() => {
        setReloadToken((token) => token + 1);
    }, []);

    const clearWriteError = useCallback((source: 'gps' | 'user') => {
        if (source === 'user' || writeErrorSourceRef.current === source) {
            writeErrorSourceRef.current = '';
            setWriteError('');
        }
    }, []);

    const reportWriteError = useCallback((source: 'gps' | 'user', error: unknown, fallback: string) => {
        console.error(fallback, error);
        writeErrorSourceRef.current = source;
        setWriteError(statusWriteMessage(error, fallback));
    }, []);

    useEffect(() => {
        const goOffline = () => setOffline(true);
        const goOnline = () => setOffline(false);
        window.addEventListener('offline', goOffline);
        window.addEventListener('online', goOnline);
        return () => {
            window.removeEventListener('offline', goOffline);
            window.removeEventListener('online', goOnline);
        };
    }, []);

    useEffect(() => {
        let ignore = false;
        // Same generation guard as a GPS write: a slower earlier poll must not
        // apply after a later getCurrentPosition has already been stamped.
        let readGeneration = 0;
        const reportGpsReadError = (error: unknown, generation: number) => {
            // Unmount and a superseded poll stamp must not surface a stale GPS failure.
            if (ignore || generation !== readGeneration) return;
            reportWriteError('gps', error, 'Your location could not be updated.');
        };
        const gettingLocation = () => {
            if (!navigator.geolocation) {
                reportGpsReadError(new Error('Geolocation is unavailable.'), readGeneration);
                return;
            }
            const generation = ++readGeneration;
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    if (ignore || generation !== readGeneration) return;
                    const { latitude, longitude } = position.coords;
                    if (techId && Number.isFinite(latitude) && Number.isFinite(longitude)) {
                        setUserLat(latitude);
                        setUserLng(longitude);
                    }
                },
                (error) => {
                    reportGpsReadError(error, generation);
                },
            );
        };
        gettingLocation();
        const getLocation = setInterval(() => {
            gettingLocation();
        }, 60000);
        return () => {
            ignore = true;
            clearInterval(getLocation);
        };
    }, [reportWriteError, techId]);

    useEffect(() => {
        const nextLocation = statusGpsWrite(userLat, userLng);
        if (!nextLocation || !techsRef || !techId) return undefined;
        const generation = ++gpsWriteGenerationRef.current;
        let cancelled = false;
        (async () => {
            // A newer reading can start before this write is sent. Skip update()
            // so an older fix cannot overwrite that newer one.
            await Promise.resolve();
            if (generation !== gpsWriteGenerationRef.current) return;
            try {
                await techsRef.doc(techId).update(nextLocation);
                if (cancelled || generation !== gpsWriteGenerationRef.current) return;
                clearWriteError('gps');
            } catch (error) {
                if (cancelled || generation !== gpsWriteGenerationRef.current) return;
                reportWriteError('gps', error, 'Your location could not be updated.');
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [clearWriteError, reportWriteError, techId, techsRef, userLat, userLng]);

    useEffect(() => {
        if (
            !selectedLocationId ||
            String(selectedLocationId || '')
                .trim()
                .toLowerCase() === 'none' ||
            !shopsRef
        ) {
            setSelectedLocation({});
            setSelectedShopError('');
            return undefined;
        }
        // A missing doc or listen error must drop the previous shop before the alert,
        // or the availability headline keeps that shop's name.
        const forgetSelectedShop = (message: string) => {
            setSelectedLocation({});
            setSelectedShopError(message);
        };
        return shopsRef.doc(selectedLocationId || '').onSnapshot(
            (documentSnapshot) => {
                const data = documentSnapshot.data();
                const exists =
                    typeof documentSnapshot.exists === 'function'
                        ? documentSnapshot.exists()
                        : documentSnapshot.exists;
                if (data && exists !== false) {
                    const shop = { ...data, id: documentSnapshot.id };
                    setSelectedLocation(shop);
                    setSelectedShopError('');
                } else {
                    forgetSelectedShop('The selected shop could not be loaded.');
                }
            },
            (error) => {
                console.error('Unable to load selected shop', error);
                forgetSelectedShop(rejectionMessage(error, 'Selected shop could not load.'));
            },
        );
    }, [selectedLocationId, shopsRef, reloadToken]);

    useEffect(() => {
        // First mount keeps the current term. A later shopsRef (region claim) must drop it,
        // or the previous search hides the new region's shops.
        if (shopsRefForSearch.current === shopsRef) return;
        shopsRefForSearch.current = shopsRef;
        setFilterTerm((current) => (current ? '' : current));
    }, [shopsRef]);

    useEffect(() => {
        if (!shopsRef || !companyId) {
            setlocations([]);
            setShopsError(
                shopsRef && !companyId
                    ? MISSING_COMPANY_CLAIM_ERROR
                    : 'Shop locations are not available for this session.',
            );
            setShopsLoading(false);
            return undefined;
        }
        setShopsLoading(true);
        setShopsError('');
        return shopsRef.where('companyId', '==', companyId).onSnapshot(
            (querySnapshot) => {
                const arrayShops: any[] = [];
                querySnapshot.forEach((documentSnapshot) => {
                    let newShops = documentSnapshot.data();
                    newShops = { ...newShops, id: documentSnapshot.id };
                    arrayShops.push(newShops);
                });
                setlocations(arrayShops);
                setShopsError('');
                setShopsLoading(false);
            },
            (error) => {
                console.error('Unable to load shop locations', error);
                setlocations([]);
                setShopsError(rejectionMessage(error, 'Shop locations could not load.'));
                setShopsLoading(false);
            },
        );
    }, [shopsRef, reloadToken, companyId]);

    useEffect(() => {
        if (!jobsRef || !techId || !companyId) {
            setJobs([]);
            setJobsError(
                jobsRef && techId && !companyId
                    ? MISSING_COMPANY_CLAIM_ERROR
                    : 'Assigned jobs are not available for this session.',
            );
            setJobsLoading(false);
            return undefined;
        }
        let cancelled = false;
        const query = jobsRef.where('companyId', '==', companyId).where('assignee', '==', techId);
        if (!jobsReadyRef.current) {
            setJobsLoading(true);
            setJobsError('');
        }
        const applySnapshot = (querySnapshot) => {
            if (cancelled) return;
            const arrayJobs: any[] = [];
            querySnapshot.forEach((documentSnapshot) => {
                let newJobs = documentSnapshot.data();
                newJobs = { ...newJobs, id: documentSnapshot.id };
                arrayJobs.push(newJobs);
            });
            setJobs(arrayJobs);
            setJobsError('');
            setJobsLoading(false);
            jobsReadyRef.current = true;
        };
        const unsubscribe = query.onSnapshot(applySnapshot, (error) => {
            if (cancelled) return;
            console.error('Unable to load assigned jobs', error);
            if (!jobsReadyRef.current) {
                setJobs([]);
                setJobsLoading(false);
            }
            setJobsError(rejectionMessage(error, 'Assigned jobs could not load.'));
        });
        const refreshFromServer = () => {
            if (cancelled || typeof query.get !== 'function') return;
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            query
                .get({ source: 'server' })
                .then(applySnapshot)
                .catch((error) => {
                    if (cancelled) return;
                    console.error('Unable to load assigned jobs', error);
                    setJobsError(rejectionMessage(error, 'Assigned jobs could not load.'));
                });
        };
        const onVisible = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            refreshFromServer();
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        return () => {
            cancelled = true;
            unsubscribe();
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, [jobsRef, techId, reloadToken, companyId]);

    const shopsSurface = statusSurfaceState({
        loading: shopsLoading,
        error: shopsError,
        offline,
        count: locations.length,
    });

    const jobsSurface = statusSurfaceState({
        loading: jobsLoading,
        error: jobsError,
        offline,
        count: jobs.length,
    });

    const shopsListCopy = statusListCopy(shopsSurface);

    const activeJobs = jobsSurface === 'ready' ? jobs.filter((job) => job.status === 'active') : [];

    const availabilityHeadline = statusAvailabilityHeadline({
        available: isAvailable === 'available',
        jobsReady: jobsSurface === 'ready',
        activeJobCount: activeJobs.length,
        locationName: selectedLocation?.name,
        hasSpecificLocation:
            Boolean(selectedLocationId) &&
            String(selectedLocationId || '')
                .trim()
                .toLowerCase() !== 'none',
    });

    const updateStatus = async (currentStatus) => {
        if (!techsRef || !techId) return;
        if (statusControlLock.current) return;
        statusControlLock.current = true;
        setStatusControlBusy(true);
        try {
            await techsRef.doc(techId).update({
                status: currentStatus === false ? 'unavailable' : 'available',
            });
            clearWriteError('user');
        } catch (error) {
            reportWriteError('user', error, 'Availability could not be updated.');
        } finally {
            statusControlLock.current = false;
            setStatusControlBusy(false);
        }
    };

    const updateSelection = useCallback(
        async (location: any) => {
            if (!techsRef || !techId) return;
            if (statusControlLock.current) return;
            statusControlLock.current = true;
            setStatusControlBusy(true);
            const clearing = selectedLocation?.id === location?.id;
            try {
                await techsRef.doc(techId).update({
                    location: clearing ? 'none' : location?.id,
                });
                setSelectedLocation(clearing ? null : location);
                clearWriteError('user');
            } catch (error) {
                reportWriteError('user', error, 'Shop selection could not be updated.');
            } finally {
                statusControlLock.current = false;
                setStatusControlBusy(false);
            }
        },
        [clearWriteError, reportWriteError, selectedLocation?.id, techId, techsRef],
    );

    const trimmedFilterTerm = (filterTerm || '').trim();
    const filteredShops = locations.filter((shop) => statusShopMatchesSearch(shop.name, trimmedFilterTerm));

    const showFilterEmpty = shopsSurface === 'ready' && Boolean(trimmedFilterTerm) && filteredShops.length === 0;

    return (
        <>
            <LoaderComponent showLoader={shopsSurface === 'loading'} />
            <div className="flex flex-col h-screen w-full" data-jobs-surface={jobsSurface} data-testid="status-page">
                <div className="bg-gray-600 py-8 text-center pl-2.5 pr-2.5 fixed w-screen z-40">
                    <p className="text-white text-xl">{availabilityHeadline}</p>
                    {writeError ? (
                        <p className="text-red-200 text-sm mt-4" role="alert" data-testid="status-write-error">
                            {writeError}
                        </p>
                    ) : null}
                    {selectedShopError ? (
                        <p className="text-red-200 text-sm mt-4" role="alert" data-testid="status-selected-shop-error">
                            {selectedShopError}
                        </p>
                    ) : null}
                    <div className="mt-6 grid grid-cols-2">
                        <div>
                            <button
                                type="button"
                                onClick={() => updateStatus(false)}
                                disabled={statusControlLock.current || statusControlBusy}
                                className={`text-white font-bold py-3 px-4 rounded focus:outline-none shadow-md${
                                    isAvailable !== 'available'
                                        ? ' bg-red-400 hover:bg-red-700'
                                        : ' bg-gray-400 hover:bg-gray-700'
                                }`}
                            >
                                Unavailable
                            </button>
                        </div>
                        <div>
                            <button
                                type="button"
                                onClick={() => updateStatus(true)}
                                disabled={statusControlLock.current || statusControlBusy}
                                className={`text-white font-bold py-3 px-4 rounded focus:outline-none shadow-md${
                                    isAvailable === 'available'
                                        ? ' bg-green-400 hover:bg-green-700'
                                        : ' bg-gray-400 hover:bg-gray-700'
                                }`}
                            >
                                Available
                            </button>
                        </div>
                    </div>
                </div>
                <div className="flex justify-center items-center">
                    <div className="mb-16 mt-56 container mx-auto text-left">
                        <div className="flex flex-col items-center">
                            {shopsSurface === 'ready' ? (
                                <div className="shadow flex w-8/12 mb-5">
                                    <input
                                        className="w-full p-2 border border-transparent focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                                        value={filterTerm}
                                        onChange={(value) => setFilterTerm(value.target.value.toLowerCase())}
                                        type="text"
                                        placeholder="Search..."
                                    />
                                    <div className="w-auto flex justify-end items-center p-2 text-purple-600">
                                        <FontAwesomeIcon className="text-2xl" icon={faSearchLocation} />
                                    </div>
                                </div>
                            ) : null}
                            {jobsSurface === 'ready' && jobsError ? (
                                <div
                                    role="alert"
                                    data-testid="jobs-refresh-error"
                                    className="py-4 bg-red-600 w-full text-white text-center mb-4"
                                >
                                    {jobsError}
                                </div>
                            ) : null}
                            {shopsSurface === 'ready' && jobsSurface === 'empty' ? (
                                <StatusEmptyCard copy={STATUS_EMPTY_JOBS} />
                            ) : null}
                            {shopsSurface === 'ready' && jobsSurface === 'error' ? (
                                <StatusEmptyCard copy={STATUS_ERROR_JOBS} detail={jobsError} onAction={retry} />
                            ) : null}
                            {shopsSurface === 'ready' && jobsSurface === 'offline' ? (
                                <StatusEmptyCard copy={STATUS_OFFLINE} onAction={retry} />
                            ) : null}
                            {shopsListCopy ? (
                                <StatusEmptyCard
                                    copy={shopsListCopy}
                                    detail={shopsSurface === 'error' ? shopsError : undefined}
                                    onAction={retry}
                                />
                            ) : null}
                            {showFilterEmpty ? (
                                <StatusEmptyCard
                                    copy={statusFilterEmptyCard(locations.length)}
                                    onAction={() => setFilterTerm('')}
                                />
                            ) : null}
                            {shopsSurface === 'ready' && !showFilterEmpty
                                ? filteredShops.map((location) => {
                                      const mapCenter = statusShopMapCenter(location.lat, location.lng);
                                      return (
                                          <div
                                              key={`location-${location.id}`}
                                              className="box-border shadow-md flex flex-col bg-blue-900 text-white mb-4 z-30 w-10/12"
                                          >
                                              {mapCenter ? (
                                                  <MapContainer
                                                      center={mapCenter}
                                                      zoom={13}
                                                      scrollWheelZoom={false}
                                                      dragging={false}
                                                      attributionControl={false}
                                                      className="max-w-full h-32 sm:h-56"
                                                  >
                                                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                                  </MapContainer>
                                              ) : null}
                                              <div className="p-4">
                                                  <p className="text-base">{location.name}</p>
                                                  <p className="text-xs">
                                                      {location.address?.street}, {location.address?.city},{' '}
                                                      {location.address?.state}
                                                  </p>
                                                  <div className="mt-4 text-center">
                                                      <button
                                                          type="button"
                                                          onClick={() => updateSelection(location)}
                                                          disabled={statusControlLock.current || statusControlBusy}
                                                          className={`text-white font-bold py-1 px-3 rounded focus:outline-none shadow-md ${
                                                              selectedLocation?.id === location.id
                                                                  ? ' bg-green-400 hover:bg-green-700'
                                                                  : ' bg-gray-400 hover:bg-gray-700'
                                                          }`}
                                                      >
                                                          {selectedLocation?.id === location.id
                                                              ? selectedLocation?.name
                                                              : 'Select Location'}
                                                      </button>
                                                  </div>
                                              </div>
                                          </div>
                                      );
                                  })
                                : null}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default StatusPage;
